<script setup lang="ts">
/**
 * CampaignMobileHero — hero plein écran mobile (carousel d'images + créateur
 * en overlay + titre + indicateurs).
 *
 * Composant PARTAGÉ (@donoor/ui) consommé en SOURCE par dashboard.donoor.org
 * et donoor.org. Couleurs en hex en dur (pas de token de thème) → rendu
 * identique dans les 2 apps.
 *
 * `<UIcon>` / `<NuxtLink>` restent globaux (résolus par l'app Nuxt qui
 * transpile ce SFC via build.transpile).
 *
 * USAGE (auto-import via wrapper local) :
 *   import CampaignMobileHero from "@donoor/ui/campaign/CampaignMobileHero.vue";
 *
 * Karim 2026-06-21 (factorisation cagnotte).
 */
// Internationalisation (lot "paquets partagés", 2026-10-11) : clés `ui.*`
// (réutilise `ui.heroImage.*`, mêmes textes que CampaignHeroImage.vue).
const { t } = useI18n();

const props = defineProps<{
  images: { id: string; url: string }[];
  title: string;
  organizationLogo: string | null;
  organizationName: string;
  organizationSlug?: string | null;
}>();

const currentIndex = ref(0);

const hasMultiple = computed(() => props.images.length > 1);

// ── Vrai swipe : track horizontal qui suit le doigt en temps réel ─────────
// Le track flex translate de `-currentIndex * 100%` au repos ; pendant le
// drag on ajoute le déplacement live du doigt (en %) → l'image bouge sous le
// doigt (feeling Instagram), pas un cross-fade. Au relâché : snap vers la
// diapo la plus proche (seuil 20% de la largeur ou vélocité suffisante).
const trackEl = ref<HTMLElement | null>(null);
const dragging = ref(false);
const dragOffsetPct = ref(0); // déplacement live en % de la largeur

let startX = 0;
let startY = 0;
let containerWidth = 1;
let lockedHorizontal = false; // true dès qu'on sait que le geste est un swipe
let decidedAxis = false; // a-t-on tranché horizontal vs vertical ?

function onTouchStart(e: TouchEvent) {
  if (!hasMultiple.value) return;
  const t = e.touches[0];
  if (!t) return;
  startX = t.clientX;
  startY = t.clientY;
  containerWidth = trackEl.value?.offsetWidth || window.innerWidth || 1;
  dragging.value = true;
  decidedAxis = false;
  lockedHorizontal = false;
  dragOffsetPct.value = 0;
}

function onTouchMove(e: TouchEvent) {
  if (!dragging.value) return;
  const t = e.touches[0];
  if (!t) return;
  const dx = t.clientX - startX;
  const dy = t.clientY - startY;

  // Décide l'axe au premier mouvement significatif : si vertical, on laisse
  // le scroll de page se faire (pas de preventDefault) et on abandonne le drag.
  if (!decidedAxis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
    decidedAxis = true;
    lockedHorizontal = Math.abs(dx) > Math.abs(dy);
    if (!lockedHorizontal) {
      dragging.value = false;
      dragOffsetPct.value = 0;
      return;
    }
  }
  if (!lockedHorizontal) return;

  // Geste horizontal confirmé → on capture (empêche le scroll vertical parasite).
  e.preventDefault();
  let pct = (dx / containerWidth) * 100;
  // Résistance élastique aux extrémités (pas de boucle infinie sur le drag live).
  const atStart = currentIndex.value === 0 && pct > 0;
  const atEnd = currentIndex.value === props.images.length - 1 && pct < 0;
  if (atStart || atEnd) pct *= 0.35;
  dragOffsetPct.value = pct;
}

function onTouchEnd() {
  if (!dragging.value) {
    dragOffsetPct.value = 0;
    return;
  }
  dragging.value = false;
  const pct = dragOffsetPct.value;
  dragOffsetPct.value = 0;
  if (!lockedHorizontal) return;
  // Snap : au-delà de 20% de la largeur, on change de diapo.
  if (pct <= -20) next();
  else if (pct >= 20) prev();
}

function prev() {
  if (currentIndex.value > 0) currentIndex.value -= 1;
}
function next() {
  if (currentIndex.value < props.images.length - 1) currentIndex.value += 1;
}
function goTo(index: number) {
  currentIndex.value = index;
}

// Position du track : base (-index*100%) + drag live. En % de la largeur du
// conteneur. Pendant le drag, transition désactivée (suivi 1:1 du doigt) ;
// au relâché, transition CSS pour le snap animé.
const trackStyle = computed(() => ({
  transform: `translate3d(calc(${-currentIndex.value * 100}% + ${dragOffsetPct.value}%), 0, 0)`,
  transition: dragging.value ? "none" : "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
}));
</script>

<template>
  <!-- SANS IMAGE : pas de cadre gris vide d'une demi-page (Karim 2026-10-08).
       Le créateur et le titre, portés d'habitude PAR l'image, passent en
       texte foncé sur le fond de page. -->
  <div v-if="images.length === 0" class="px-4 pt-4">
    <NuxtLink
      :to="organizationSlug ? `/associations/${organizationSlug}` : undefined"
      class="inline-flex max-w-full items-center gap-2.5"
      :class="{ 'pointer-events-none': !organizationSlug }"
    >
      <div
        class="w-9 h-9 shrink-0 rounded-full bg-white flex items-center justify-center overflow-hidden ring-1 ring-gray-200"
      >
        <img
          v-if="organizationLogo"
          :src="organizationLogo"
          :alt="organizationName"
          class="w-full h-full object-contain"
        />
        <span v-else class="text-gray-700 text-sm font-bold">
          {{ organizationName.charAt(0).toUpperCase() }}
        </span>
      </div>
      <span class="min-w-0 truncate text-gray-900 font-semibold text-sm">
        {{ organizationName }}
      </span>
    </NuxtLink>
    <h1 class="mt-3 text-gray-900 text-3xl sm:text-4xl font-bold leading-tight">
      {{ title }}
    </h1>
  </div>

  <div
    v-else
    class="relative w-full aspect-15/16 sm:aspect-4/3 overflow-hidden bg-gray-100"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchEnd"
  >
    <!-- Images : track flex qui translate horizontalement (vrai swipe qui suit
         le doigt). `touch-action: pan-y` laisse le scroll vertical de page se
         faire ; on ne capture que l'axe horizontal (voir onTouchMove). -->
    <div
      ref="trackEl"
      class="absolute inset-0 flex h-full will-change-transform [touch-action:pan-y]"
      :style="trackStyle"
    >
      <div
        v-for="(image, index) in images"
        :key="image.id"
        class="relative w-full h-full shrink-0"
      >
        <!-- object-cover : l'image remplit tout le cadre, recadrée au centre.
             Cadre quasi carré sur mobile (15/16) → coupe minimale, rendu net
             et plein, zéro bande. -->
        <img
          :src="image.url"
          :alt="t('ui.heroImage.imageAlt', { numero: index + 1 })"
          :loading="index === 0 ? 'eager' : 'lazy'"
          :fetchpriority="index === 0 ? 'high' : 'auto'"
          :decoding="index === 0 ? 'sync' : 'async'"
          draggable="false"
          class="absolute inset-0 w-full h-full object-cover select-none"
        />
      </div>
    </div>

    <!-- Gradient haut (créateur lisible) -->
    <div
      class="absolute inset-x-0 top-0 h-32 bg-linear-to-b from-black/50 to-transparent pointer-events-none"
    />
    <!-- Gradient bas (titre lisible) -->
    <div
      class="absolute inset-x-0 bottom-0 h-56 bg-linear-to-t from-black/80 via-black/40 to-transparent pointer-events-none"
    />

    <!-- Créateur en overlay top-left -->
    <NuxtLink
      :to="organizationSlug ? `/associations/${organizationSlug}` : undefined"
      class="absolute top-4 left-4 inline-flex items-center gap-2.5"
      :class="{ 'pointer-events-none': !organizationSlug }"
    >
      <div
        class="w-9 h-9 rounded-full bg-white flex items-center justify-center overflow-hidden ring-2 ring-white/40 shadow-sm"
      >
        <img
          v-if="organizationLogo"
          :src="organizationLogo"
          :alt="organizationName"
          class="w-full h-full object-contain"
        />
        <span v-else class="text-gray-700 text-sm font-bold">
          {{ organizationName.charAt(0).toUpperCase() }}
        </span>
      </div>
      <span class="text-white font-semibold text-sm drop-shadow-md truncate max-w-[200px]">
        {{ organizationName }}
      </span>
    </NuxtLink>

    <!-- Titre en overlay bottom -->
    <h1
      class="absolute bottom-12 left-4 right-4 text-center text-white text-3xl sm:text-4xl font-bold leading-tight drop-shadow-lg"
    >
      {{ title }}
    </h1>

    <!-- Indicateurs carousel -->
    <div
      v-if="hasMultiple"
      class="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5"
    >
      <button
        v-for="(image, index) in images"
        :key="image.id"
        type="button"
        class="h-1.5 rounded-full transition-all"
        :class="index === currentIndex ? 'w-5 bg-white' : 'w-1.5 bg-white/50'"
        :aria-label="t('ui.heroImage.allerALImage', { numero: index + 1 })"
        @click="goTo(index)"
      />
    </div>
  </div>
</template>
